import { useMemo } from "react";
import {
  useGetCommentsQuery,
  useGetLifecycleEventsQuery,
} from "app/api/companies";
import { Company } from "models/Company";
import { CompanyLifecycleEvent } from "models/CompanyLifecycleEvent";
import { CompanyComment } from "models/CompanyComment";

type LogEntry = CompanyLifecycleEvent | CompanyComment;

const groupEventsByDate = (entries: LogEntry[]) => {
  const grouped = new Map<string, LogEntry[]>();
  entries.forEach((entry) => {
    const date = new Date(entry.createdAt).toLocaleDateString();
    if (!grouped.has(date)) {
      grouped.set(date, []);
    }
    grouped.get(date)?.push(entry);
  });
  return grouped;
};

export function useLogEntriesByDate(companyId: Company["id"]) {
  const { data: lifecycleEvents, isLoading: isLoadingEvents } = useGetLifecycleEventsQuery({
    id: companyId,
  });

  const { data: comments, isLoading: isLoadingComments } = useGetCommentsQuery({ id: companyId });

  const groupedEvents = useMemo(() => {
    if (!lifecycleEvents || !comments) return new Map<string, LogEntry[]>();

    return groupEventsByDate(
      [...lifecycleEvents, ...comments].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      )
    );
  }, [lifecycleEvents, comments]);

  return { data: groupedEvents, isLoading: isLoadingEvents || isLoadingComments };
}
